"use client";

import React, { useState, useRef, useEffect, Suspense } from "react";
import { Check, AlertCircle, Loader2, ArrowRight, Calendar } from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useSearchParams } from "next/navigation";

type Status = "idle" | "submitting" | "success" | "error";

function ContactFormInner() {
  const searchParams = useSearchParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    organizationType: "",
    service: "",
    budget: "",
    website: "",
    message: "",
    honeypot: "",
  });

  useEffect(() => {
    const serviceParam = searchParams.get("service");
    const budgetParam = searchParams.get("budget");
    const planParam = searchParams.get("plan")?.toLowerCase();

    let autoService = serviceParam;

    // Handle plan shorthand (e.g. ?plan=basic)
    if (planParam === "basic") autoService = "Laurel Basic Care Plan";
    if (planParam === "standard") autoService = "Laurel Standard Care Plan";
    if (planParam === "premium") autoService = "Laurel Premium Care Plan";

    if (autoService || budgetParam) {
      setFormData(prev => ({
        ...prev,
        service: autoService || prev.service,
        budget: budgetParam || prev.budget,
      }));
    }
  }, [searchParams]);

  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  
  const errorSummaryRef = useRef<HTMLDivElement>(null);
  const successHeadingRef = useRef<HTMLHeadingElement>(null);

  const MAX_MESSAGE_CHARS = 2000;

  // Validation
  const validate = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = "Name is required.";
    if (!formData.email.trim()) {
      errors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address.";
    }
    if (!formData.message.trim()) errors.message = "Message is required.";
    if (formData.message.length > MAX_MESSAGE_CHARS) {
      errors.message = `Message must be under ${MAX_MESSAGE_CHARS} characters.`;
    }
    return errors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    if (status === "error") setStatus("idle");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setStatus("error");
      setErrorMessage("Please correct the errors in the form below.");
      return;
    }

    setStatus("submitting");
    setErrorMessage("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send message.");
      }

      setStatus("success");
    } catch (err: any) {
      setErrorMessage(err.message);
      setStatus("error");
    }
  };

  // Focus management
  useEffect(() => {
    if (status === "error" && errorSummaryRef.current) {
      errorSummaryRef.current.focus();
    }
    if (status === "success" && successHeadingRef.current) {
      successHeadingRef.current.focus();
    }
  }, [status]);

  if (status === "success") {
    return (
      <div 
        role="status" 
        aria-live="polite"
        className="bg-bloom-green-deep rounded-[24px] border border-bloom-green-mid/20 p-10 text-center shadow-xl animate-in fade-in zoom-in duration-500 text-white"
      >
        <div className="flex justify-center mb-6" aria-hidden="true">
          <div className="w-16 h-16 rounded-full bg-bloom-gold/20 flex items-center justify-center text-bloom-gold">
            <Check size={32} />
          </div>
        </div>
        <h2 
          ref={successHeadingRef}
          tabIndex={-1}
          className="font-serif text-3xl font-bold text-white mb-4 outline-none"
        >
          Message sent
        </h2>
        <p className="font-sans text-white/80 max-w-sm mx-auto leading-relaxed mb-8">
          Thank you, {formData.name}. We&apos;ve received your inquiry and will be in touch within one business day.
        </p>
        <div className="flex flex-col gap-4 items-center">
          <Link
            href="/book"
            className="inline-flex items-center gap-2 bg-bloom-gold text-white px-6 py-3 rounded-lg font-sans font-bold hover:bg-bloom-gold-dark transition-all shadow-md"
          >
            Book a discovery call <ArrowRight size={16} />
          </Link>
          <button
            onClick={() => {
              setFormData({ name: "", email: "", organization: "", organizationType: "", service: "", budget: "", website: "", message: "", honeypot: "" });
              setStatus("idle");
            }}
            className="text-[12px] text-white/50 font-sans hover:text-white underline underline-offset-4"
          >
            Send another message
          </button>
        </div>
      </div>
    );
  }

  const charCount = formData.message.length;
  const charCounterColor = charCount >= 1950 ? "text-[#c45a5a]" : charCount >= 1800 ? "text-bloom-gold" : "text-bloom-muted/60";

  return (
    <div className="bg-white rounded-[32px] border-2 border-bloom-linen p-8 md:p-12 shadow-none max-w-[640px] mx-auto text-bloom-charcoal">
      {status === "error" && (
        <div 
          ref={errorSummaryRef}
          tabIndex={-1}
          role="alert"
          className="mb-8 p-4 bg-red-50 border border-red-100 rounded-xl outline-none"
        >
          <h3 className="font-sans text-sm font-bold text-[#c45a5a] mb-2 flex items-center gap-2">
            <AlertCircle size={16} />
            There were errors with your submission
          </h3>
          <ul className="list-disc list-inside font-sans text-xs text-[#c45a5a] space-y-1">
            {Object.entries(fieldErrors).map(([key, msg]) => (
              <li key={key}>{msg}</li>
            ))}
            {errorMessage && !Object.keys(fieldErrors).length && <li>{errorMessage}</li>}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-6" aria-label="Contact form">
        {/* Honeypot field — visually hidden */}
        <div className="absolute left-[-9999px] h-0 opacity-0 overflow-hidden" aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input
            id="website"
            name="honeypot"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={formData.honeypot}
            onChange={handleChange}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Name */}
          <div className="space-y-1.5">
            <Label 
              htmlFor="name" 
              className="text-bloom-green-deep font-bold uppercase tracking-widest text-xs ml-0.5"
            >
              Name <span className="text-[#c45a5a]" aria-hidden="true">*</span>
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              required
              aria-required="true"
              aria-invalid={fieldErrors.name ? "true" : "false"}
              aria-describedby={fieldErrors.name ? "name-error" : undefined}
              autoComplete="name"
              disabled={status === "submitting"}
              value={formData.name}
              onChange={handleChange}
              className={cn(
                "h-12 bg-bloom-cream/30 border-bloom-linen text-bloom-charcoal placeholder:text-bloom-muted/40 focus:ring-bloom-gold focus:border-bloom-gold",
                fieldErrors.name && "border-[#c45a5a] focus:ring-[#c45a5a] focus:border-[#c45a5a]"
              )}
            />
            {fieldErrors.name && (
              <p id="name-error" className="text-[#c45a5a] text-xs font-medium ml-0.5">{fieldErrors.name}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <Label 
              htmlFor="email" 
              className="text-bloom-green-deep font-bold uppercase tracking-widest text-xs ml-0.5"
            >
              Email <span className="text-[#c45a5a]" aria-hidden="true">*</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              aria-required="true"
              aria-invalid={fieldErrors.email ? "true" : "false"}
              aria-describedby={fieldErrors.email ? "email-error" : undefined}
              autoComplete="email"
              disabled={status === "submitting"}
              value={formData.email}
              onChange={handleChange}
              className={cn(
                "h-12 bg-bloom-cream/30 border-bloom-linen text-bloom-charcoal placeholder:text-bloom-muted/40 focus:ring-bloom-gold focus:border-bloom-gold",
                fieldErrors.email && "border-[#c45a5a] focus:ring-[#c45a5a] focus:border-[#c45a5a]"
              )}
            />
            {fieldErrors.email && (
              <p id="email-error" className="text-[#c45a5a] text-xs font-medium ml-0.5">{fieldErrors.email}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Organization Type */}
          <div className="space-y-1.5">
            <Label 
              htmlFor="organizationType" 
              className="text-bloom-green-deep font-bold uppercase tracking-widest text-xs ml-0.5"
            >
              Organization Type
            </Label>
            <select
              id="organizationType"
              name="organizationType"
              disabled={status === "submitting"}
              value={formData.organizationType}
              onChange={handleChange}
              className="w-full h-12 rounded-lg border border-bloom-linen bg-bloom-cream/30 px-3 font-sans text-sm text-bloom-charcoal focus:ring-2 focus:ring-bloom-gold focus:outline-none transition-all cursor-pointer"
            >
              <option value="" className="bg-white">Select one...</option>
              <option value="Small Business & Local Trades" className="bg-white">Small Business & Local Trades</option>
              <option value="Nonprofit" className="bg-white">Nonprofit & 501(c)(3)</option>
              <option value="HOA" className="bg-white">HOA & Community</option>
              <option value="Church" className="bg-white">Church & Faith Organization</option>
              <option value="Local Government" className="bg-white">Local Government</option>
              <option value="Small Business" className="bg-white">Small Business</option>
              <option value="Other" className="bg-white">Other</option>
            </select>
            {formData.organizationType === "Small Business" && (
              <p className="text-xs text-bloom-muted mt-2 italic animate-in fade-in slide-in-from-top-1">
                We take select small business projects. Tell us about your organization and goals and we'll be in touch within one business day.
              </p>
            )}
          </div>

          {/* Organization */}
          <div className="space-y-1.5">
            <Label 
              htmlFor="organization" 
              className="text-bloom-green-deep font-bold uppercase tracking-widest text-xs ml-0.5"
            >
              Organization Name
            </Label>
            <Input
              id="organization"
              name="organization"
              type="text"
              disabled={status === "submitting"}
              value={formData.organization}
              onChange={handleChange}
              placeholder="Your organization name"
              className="h-12 bg-bloom-cream/30 border-bloom-linen text-bloom-charcoal placeholder:text-bloom-muted/40 focus:ring-bloom-gold focus:border-bloom-gold"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Website URL */}
          <div className="space-y-1.5">
            <Label 
              htmlFor="website-url" 
              className="text-bloom-green-deep font-bold uppercase tracking-widest text-xs ml-0.5"
            >
              Current Website (Optional)
            </Label>
            <Input
              id="website-url"
              name="website"
              type="url"
              disabled={status === "submitting"}
              value={formData.website}
              onChange={handleChange}
              placeholder="https://example.com"
              className="h-12 bg-bloom-cream/30 border-bloom-linen text-bloom-charcoal placeholder:text-bloom-muted/40 focus:ring-bloom-gold focus:border-bloom-gold"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Service Interest */}
          <div className="space-y-1.5">
            <Label 
              htmlFor="service" 
              className="text-bloom-green-deep font-bold uppercase tracking-widest text-xs ml-0.5"
            >
              Service interest
            </Label>
            <select
              id="service"
              name="service"
              disabled={status === "submitting"}
              value={formData.service}
              onChange={handleChange}
              className="w-full h-12 rounded-lg border border-bloom-linen bg-bloom-cream/30 px-3 font-sans text-sm text-bloom-charcoal focus:ring-2 focus:ring-bloom-gold focus:outline-none transition-all cursor-pointer"
            >
              <option value="" className="bg-white">Select one...</option>
              <option value="I need a new website" className="bg-white">I need a new website</option>
              <option value="I need a website redesign" className="bg-white">I need a website redesign</option>
              <option value="I need an accessibility audit" className="bg-white">I need an accessibility audit</option>
              <option value="I'm interested in templates" className="bg-white">I'm interested in templates</option>
              <option value="Foundation Package" className="bg-white">Foundation Package</option>
              <option value="Growth Package" className="bg-white">Growth Package</option>
              <option value="Signature Package" className="bg-white">Signature Package</option>
              <option value="Laurel Basic Care Plan" className="bg-white">Laurel Basic Care Plan</option>
              <option value="Laurel Standard Care Plan" className="bg-white">Laurel Standard Care Plan</option>
              <option value="Laurel Premium Care Plan" className="bg-white">Laurel Premium Care Plan</option>
              <option value="Something else" className="bg-white">Something else</option>
            </select>
          </div>

          {/* Budget Range */}
          <div className="space-y-1.5">
            <Label 
              htmlFor="budget" 
              className="text-bloom-green-deep font-bold uppercase tracking-widest text-xs ml-0.5"
            >
              Budget range
            </Label>
            <select
              id="budget"
              name="budget"
              disabled={status === "submitting"}
              value={formData.budget}
              onChange={handleChange}
              className="w-full h-12 rounded-lg border border-bloom-linen bg-bloom-cream/30 px-3 font-sans text-sm text-bloom-charcoal focus:ring-2 focus:ring-bloom-gold focus:outline-none transition-all cursor-pointer"
            >
              <option value="" className="bg-white">Select one...</option>
              <option value="Under $2,500" className="bg-white">Under $2,500</option>
              <option value="$2,500 – $5,000" className="bg-white">$2,500 – $5,000</option>
              <option value="$5,000 – $10,000" className="bg-white">$5,000 – $10,000</option>
              <option value="$10,000+" className="bg-white">$10,000+</option>
              <option value="Not sure yet" className="bg-white">Not sure yet</option>
            </select>
          </div>
        </div>

        {/* Message */}
        <div className="space-y-1.5">
          <div className="flex justify-between items-end mb-1">
            <Label 
              htmlFor="message" 
              className="text-bloom-green-deep font-bold uppercase tracking-widest text-xs ml-0.5"
            >
              Message <span className="text-[#c45a5a]" aria-hidden="true">*</span>
            </Label>
            <span 
              id="message-counter"
              aria-live="polite"
              className={cn("font-sans text-xs tracking-wider", charCounterColor)}
            >
              {charCount} / {MAX_MESSAGE_CHARS}
            </span>
          </div>
          <Textarea
            id="message"
            name="message"
            required
            aria-required="true"
            aria-invalid={fieldErrors.message ? "true" : "false"}
            aria-describedby={cn(fieldErrors.message ? "message-error" : undefined, "message-counter")}
            rows={5}
            disabled={status === "submitting"}
            value={formData.message}
            onChange={handleChange}
            className={cn(
              "bg-bloom-cream/30 border-bloom-linen text-bloom-charcoal placeholder:text-bloom-muted/40 focus:ring-bloom-gold focus:border-bloom-gold min-h-[120px] resize-none",
              fieldErrors.message && "border-[#c45a5a] focus:ring-[#c45a5a] focus:border-[#c45a5a]"
            )}
            placeholder="Tell us about your project or mission..."
          />
          {fieldErrors.message && (
            <p id="message-error" className="text-[#c45a5a] text-xs font-medium ml-0.5">{fieldErrors.message}</p>
          )}
        </div>

        <Button
          type="submit"
          disabled={status === "submitting"}
          aria-busy={status === "submitting"}
          className="w-full h-14 bg-bloom-gold hover:bg-bloom-gold-dark text-white font-sans font-bold uppercase tracking-widest text-sm transition-all shadow-lg hover:shadow-xl active:scale-[0.98] focus-bloom rounded-xl"
        >
          {status === "submitting" ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending Message…
            </>
          ) : (
            "Send message"
          )}
        </Button>
      </form>
    </div>
  );
}

export default function ContactForm() {
  return (
    <Suspense fallback={<div className="h-[600px] w-full bg-[#fefcf9] rounded-[16px] animate-pulse" />}>
      <ContactFormInner />
    </Suspense>
  );
}
